import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { memberApi } from "@/lib/memberApi";
import { useLang } from "@/context/LanguageContext";
import { LeafLogo } from "@/components/common/LeafLogo";
import { formatDate } from "@/lib/dateFormat";
import { Button } from "@/components/ui/button";
import { toast, Toaster } from "sonner";
import { Download, ArrowLeft } from "lucide-react";

const inr = (n) => `₹${Number(n).toLocaleString("en-IN")}`;

const MemberReceipt = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { lang } = useLang();
  const [r, setR] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => { memberApi.receipt(id).then(setR).catch(() => setR(false)); }, [id]);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const blob = await memberApi.receiptPdf(id);
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch { toast.error("Could not open the PDF. Please try again."); }
    finally { setDownloading(false); }
  };

  if (r === false) return <div className="flex min-h-screen items-center justify-center text-gmoaa-text-muted">Receipt not found.</div>;
  if (!r) return <div className="flex min-h-screen items-center justify-center text-gmoaa-text-muted">Loading…</div>;

  return (
    <div className="min-h-screen bg-gmoaa-surface py-8 print:bg-white print:py-0">
      <Toaster position="top-center" richColors />
      <div className="mx-auto max-w-2xl px-4 print:px-0">
        <div className="mb-4 flex items-center justify-between print:hidden">
          <Button variant="ghost" onClick={() => navigate("/member")}><ArrowLeft size={16} className="mr-1" /> Back</Button>
          <Button data-testid="download-receipt-pdf" disabled={downloading} onClick={downloadPdf} className="bg-gmoaa-green text-white hover:bg-gmoaa-green-hover"><Download size={16} className="mr-1.5" /> {downloading ? "Preparing…" : "Download official PDF"}</Button>
        </div>

        {/* Receipt — gold double border */}
        <div className="relative rounded-lg bg-white p-1" style={{ border: "3px double #C9A24A" }}>
          <div className="rounded p-8" style={{ border: "1px solid #C9A24A" }}>
            <div className="flex items-center justify-center gap-4 border-b border-gmoaa-border-subtle pb-5 text-center">
              <LeafLogo size={64} />
              <div>
                <p className="font-gujarati text-xl font-bold text-gmoaa-green">મેડિકલ ઓફિસર્સ (આયુર્વેદ) એસોસિએશન, ગુજરાત રાજ્ય</p>
                <p className="text-sm text-gmoaa-text-muted">Medical Officers (Ayurved) Association, Gujarat State</p>
                <p className="text-xs text-gmoaa-text-muted">Gandhinagar · Reg. A.D.R 1086/6605/CHHA</p>
              </div>
            </div>

            <div className="mt-5 flex items-center justify-between">
              <h2 className="font-serif text-lg font-semibold text-gmoaa-green">Payment Receipt</h2>
              {r.status === "cancelled"
                ? <span className="rounded bg-gmoaa-red-bg px-2 py-0.5 text-sm font-bold text-gmoaa-red">CANCELLED</span>
                : <span className="text-sm font-semibold text-gmoaa-saffron-hover">{r.number}</span>}
            </div>

            <table className="mt-4 w-full text-sm">
              <tbody>
                {[
                  ["Received from", lang === "gu" ? (r.member_name_gu || r.member_name_en) : r.member_name_en],
                  ["District", r.district],
                  ["Purpose", lang === "gu" ? (r.purpose_gu || r.purpose_en) : r.purpose_en],
                  ["Payment method", r.method === "razorpay" ? "Online (Razorpay)" : "Offline / recorded"],
                  ["Payment ID", r.payment_id],
                  ["Date", formatDate(r.created_at)],
                  ["Financial year", r.fy],
                ].map(([k, v]) => (
                  <tr key={k} className="border-b border-gmoaa-border-subtle/60">
                    <td className="py-2 text-gmoaa-text-muted">{k}</td>
                    <td className="py-2 text-right font-medium text-gmoaa-text-dark">{v}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="mt-4 flex items-center justify-between rounded-lg bg-gmoaa-gold-light px-4 py-3">
              <span className="font-serif text-lg font-semibold text-gmoaa-green">Amount</span>
              <span className="font-serif text-2xl font-bold text-gmoaa-green">{inr(r.amount)}</span>
            </div>

            <div className="mt-8 flex items-end justify-between">
              <p className="text-xs text-gmoaa-text-muted">This is a computer-generated receipt.<br />Disputes subject to Gandhinagar, Gujarat jurisdiction.</p>
              <div className="text-center"><div className="h-10 w-32 border-b border-gmoaa-text-dark" /><p className="mt-1 text-xs text-gmoaa-text-muted">Treasurer</p></div>
            </div>
            <p className="mt-6 text-center text-xs text-gmoaa-gold">www.gmoaainfo.org</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MemberReceipt;
